// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { useEffect, useState } from "react";
import { Engine } from "sdv/engine/Engine";
import { setSelectionMode } from "store/documents/actions";
import { CanvasSelectionMode } from "store/documents/types";
import { selectCurrentChunkInstance } from "store/sdvSelectors";
import { setEditOperation } from "store/sdvSlice";
import { useChunkSelection } from "./chunkSelection";
import { useConfirmLabel } from "./ConfirmLabel";
import { DgmlInstancesSingleton } from "./documentDgml";
import { useLabelChunk } from "./LabelChunk";
import { useLabelChunks } from "./LabelChunks";
import { useMultiSelectChunks } from "./MultiSelectChunks";
import { useAppDispatch, useAppSelector } from "./redux";
import { useSdvResource } from "./SdvResource";
import { arrayEquals } from "utils/ArrayUtils";

export function useEditLabel(documentId: string, engine: Engine | null) {
    const canvasSelectionMode = useAppSelector(state => state.documents.selectionMode);
    const [restoreSelectionMode, setRestoreSelectionMode] = useState<CanvasSelectionMode>(canvasSelectionMode);

    const { clusterId: docsetId, fileId } = createFolderId(documentId);

    const selectedInstance = useAppSelector(selectCurrentChunkInstance);
    const { getChunkLabel } = useLabelChunk(docsetId, documentId);
    const { selectChunk } = useChunkSelection();

    const { isActive: isMultiSelectActive, startMultiSelect, stopMultiSelect, spanIds: multiSelectChunks, removeMultiSelectChunk } = useMultiSelectChunks(documentId, engine);
    const { label, isEditing, chunks: labelChunks } = useLabelChunks(documentId, isMultiSelectActive);
    const { confirm } = useConfirmLabel(documentId, label ?? '');

    function removeChunk(spanId: string) {
        if (!isMultiSelectActive) {
            DgmlInstancesSingleton.removeLabel({
                docsetId: docsetId,
                fileId: fileId,
                spanId: spanId
            })

            if (selectedInstance?.spanId === spanId) {
                // Ensure that the label remains selected by selecting another label chunk if available
                const targetLabelChunk = labelChunks.find(x => x !== spanId);
                if (!!targetLabelChunk) {
                    selectChunk(targetLabelChunk);
                }
            }
        }
        else {
            removeMultiSelectChunk(spanId);
        }
    }

    const { set: setResizeDisabled, value: resizeDisabled } = useSdvResource('resizeDisabled', engine);
    useEffect(() => {
        if (!resizeDisabled && isEditing) {
            setResizeDisabled(true);
        }
    }, [setResizeDisabled, isEditing, resizeDisabled])

    const dispatch = useAppDispatch();

    function startEdit() {
        startMultiSelect(labelChunks);
        setResizeDisabled(true);
        // Save the canvas selection mode state while switching to highlighter on edit
        setRestoreSelectionMode(canvasSelectionMode);
        dispatch(setSelectionMode(CanvasSelectionMode.Highlight));
    }

    function stopEdit() {
        if ((!selectedInstance || getChunkLabel(selectedInstance) !== label) && !!labelChunks.length) {
            // When exiting edit mode, ensure that the label remains selected
            selectChunk(labelChunks[0]);
        }

        stopMultiSelect();
        setResizeDisabled(false);

        // Reset the canvas selection mode to its state before editing
        if (restoreSelectionMode !== canvasSelectionMode && canvasSelectionMode !== CanvasSelectionMode.TextEdit) {
            dispatch(setSelectionMode(restoreSelectionMode));
        }

        // Reset the edit state
        dispatch(setEditOperation());
    }

    function confirmEdit(additionalSpanIds?: string[]) {
        if (!isMultiSelectActive) {
            return;
        }

        confirm(!!additionalSpanIds?.length ? [...multiSelectChunks, ...additionalSpanIds] : multiSelectChunks);
        stopEdit();
    }

    return {
        selectedLabel: label,
        isEditing: isEditing,
        editSpanIds: multiSelectChunks,
        hasEdits: isEditing && !arrayEquals(multiSelectChunks, labelChunks),
        spanIds: isEditing ? multiSelectChunks : labelChunks,
        count: isEditing ? multiSelectChunks.length : labelChunks.length,
        selectChunk: selectChunk,
        editDisabled: false,
        startLabelEdit: startEdit,
        stopLabelEdit: stopEdit,
        confirmEdit: confirmEdit,
        removeChunk: removeChunk
    }
}
